import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SectionHeader, KpiCard } from "@/components/common/Widgets";
import { Building2, Users, FileText, CheckCircle2, ShieldCheck, Plus, Settings } from "lucide-react";
import { fmtNum } from "@/lib/format";
import { useNavigate } from "react-router-dom";

export default function SuperAdminPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/super-admin/overview"); setOverview(r.data); } catch (e) { toast.error("Failed to load overview"); } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-5">
      <SectionHeader title="Super Admin Console" subtitle="Manage multiple banks (tenants) and their configuration." action={(
        <Button onClick={() => navigate("/super-admin/banks/new")} data-testid="sa-new-bank-button"><Plus className="h-4 w-4 mr-1" />Onboard New Bank</Button>
      )} />
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard icon={Building2} label="Banks" value={overview?.total_banks ?? 0} testid="sa-kpi-banks" />
            <KpiCard icon={Users} label="Total Users" value={overview?.total_users ?? 0} />
            <KpiCard icon={Users} label="Total Customers" value={overview?.total_customers ?? 0} />
            <KpiCard icon={FileText} label="Applications" value={overview?.total_applications ?? 0} />
            <KpiCard icon={CheckCircle2} label="Sanctioned" value={overview?.sanctioned ?? 0} accent="accent" />
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-muted/30 text-sm font-semibold heading">Banks (Tenants)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Short</th><th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5">Branches</th><th className="text-right px-3 py-2.5">Users</th><th className="text-right px-3 py-2.5">Customers</th><th className="text-right px-3 py-2.5">Applications</th><th className="text-right px-3 py-2.5">Sanctioned</th><th></th></tr></thead>
                <tbody>
                  {(overview?.per_bank || []).length === 0 && <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">No banks yet — onboard your first.</td></tr>}
                  {(overview?.per_bank || []).map((b) => (
                    <tr key={b.bank_id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">{b.name}</td>
                      <td className="px-3 py-2 mono text-xs">{b.short_name}</td>
                      <td className="px-3 py-2">{b.is_active ? <Badge variant="outline" className="text-success border-success/40"><ShieldCheck className="h-3 w-3 mr-1 inline" />Active</Badge> : <Badge variant="outline" className="text-destructive border-destructive/40">Inactive</Badge>}</td>
                      <td className="px-3 py-2 num-right">{b.branches}</td>
                      <td className="px-3 py-2 num-right">{b.users}</td>
                      <td className="px-3 py-2 num-right">{b.customers}</td>
                      <td className="px-3 py-2 num-right">{b.applications}</td>
                      <td className="px-3 py-2 num-right text-success">{b.sanctioned}</td>
                      <td className="px-3 py-2 text-right"><Link to={`/super-admin/banks/${b.bank_id}`}><Button size="sm" variant="outline" data-testid={`sa-manage-${b.bank_id}`}><Settings className="h-3.5 w-3.5 mr-1" />Manage</Button></Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
